/**
 * ReviewSlipEditor — the expanded part of a Review pick row (specs/challenge-flow-redesign AC-4.7).
 * Shows only the outcomes of that pick's bet type and swaps the pick in place. Mounted only for the
 * expanded row, so match details load lazily, like the build card's ExpandedMarkets.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, ActivityIndicator, Platform } from 'react-native';
import { MarketOddsRenderer } from '../CreateGamesStep/MarketOddsRenderer';
import { mergeOverUnderByFamily } from '../MatchDetailsScreen/overUnderGrouping.helper';
import { useMatchDetails } from '../../../src/create/hooks/useMatchDetails';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { useIsProUser } from '../../../src/shared/hooks/useIsProUser';
import { useDialog } from '../../../src/shared/hooks/useDialog';
import { filterMarketsForIosCreate } from '../../../src/create/helpers/premiumMarketsIosDefer.helper';
import { dropUnpriceableMarkets } from '../../../src/create/helpers/priceableMarkets.helper';
import { findSlipMarket, shouldApplyReviewEdit } from '../../../src/create/helpers/pickIndicators.helper';
import { getSportDefinitionOrDefault } from '../../../src/sports/sports.constants';
import type { Game, PickSlip } from '../../../src/create/types/create.types';
import { reviewSlipEditorStyles as styles } from './ReviewSlipEditor.styles';

export interface ReviewSlipEditorProps {
  slip: PickSlip;
  game: Game | undefined;
  homeName: string;
  awayName: string;
}

export const ReviewSlipEditor: React.FC<ReviewSlipEditorProps> = ({ slip, game, homeName, awayName }) => {
  const { match, loading } = useMatchDetails(slip.gameId);
  const { handleAddOtherPick, isOtherPickSelected } = useCreatePage();
  const isPro = useIsProUser();
  const { showAlert } = useDialog();

  // Same market pipeline as InlineMarketPicker, so the editor offers exactly what the build card did.
  const market = useMemo(() => {
    const raw = match?.odds?.markets ?? [];
    const merged = mergeOverUnderByFamily(
      dropUnpriceableMarkets(Platform.OS === 'ios' ? filterMarketsForIosCreate(raw) : raw),
    );
    return findSlipMarket(merged, slip, getSportDefinitionOrDefault(game?.sport).primaryMarketId);
  }, [match, slip, game?.sport]);

  const onPick = useCallback(
    (gameId: string, marketId: number, marketName: string, value: string, odd: number) => {
      if (!shouldApplyReviewEdit(isOtherPickSelected, gameId, marketId, value)) return;
      handleAddOtherPick(gameId, marketId, marketName, value, odd, homeName, awayName);
    },
    [isOtherPickSelected, handleAddOtherPick, homeName, awayName]
  );

  const onPremiumBlock = useCallback(() => {
    showAlert({
      title: 'Pro Feature',
      message: 'Upgrade to Zporti Pro to unlock this prediction type.',
    });
  }, [showAlert]);

  if (loading) {
    return (
      <View style={styles.status}>
        <ActivityIndicator size="small" color="#A78BFA" />
      </View>
    );
  }

  if (market == null) {
    return (
      <View style={styles.status}>
        <Text style={styles.statusText}>No longer available</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MarketOddsRenderer
        market={market}
        gameId={slip.gameId}
        homeName={homeName}
        awayName={awayName}
        isSelected={isOtherPickSelected}
        onPick={onPick}
        isPro={isPro}
        onPremiumBlock={onPremiumBlock}
      />
    </View>
  );
};
